
import { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useLanguage } from '@/contexts/LanguageContext';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';

const Privacy = () => {
  const { language } = useLanguage();
  
  const pageTitle = language === 'en' ? 'Privacy Policy | Attaboy Intelligence' : 'Politique de Confidentialité | Attaboy Intelligence Artificielle';
  const pageDescription = language === 'en' 
    ? 'Our commitment to protecting your privacy and data.' 
    : 'Notre engagement à protéger votre vie privée et vos données.';

  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
      </Helmet>
      
      <div className="flex flex-col min-h-screen">
        <Navbar />
        <main className="flex-grow pt-24">
          <div className="container mx-auto px-4 py-12 max-w-4xl">
            <h1 className="text-4xl md:text-5xl font-bold mb-10 font-poppins text-attaboy-black">
              {language === 'en' ? 'Privacy Policy' : 'Politique de Confidentialité'}
            </h1>
            
            {language === 'en' ? (
              <div className="prose prose-lg max-w-none">
                <p className="text-lg mb-6">Last updated: May 19, 2025</p>
                
                <h2 className="text-2xl font-bold mt-10 mb-4">Introduction</h2>
                <p>
                  At Attaboy Intelligence ("we", "our", or "us"), we respect your privacy and are committed to protecting your personal data. 
                  This privacy policy explains how we collect, use, and safeguard your information when you visit our website or use our AI services.
                </p>
                
                <h2 className="text-2xl font-bold mt-10 mb-4">Information We Collect</h2>
                <p>We may collect several types of information, including but not limited to:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Personal information</strong>: Name, email address, phone number, and other contact details you provide.</li>
                  <li><strong>Usage information</strong>: How you interact with our website and services, including features used and time spent.</li>
                  <li><strong>Device information</strong>: IP address, browser type, device type, and operating system.</li>
                  <li><strong>Content data</strong>: Information you input into our AI tools or services.</li>
                </ul>
                
                <h2 className="text-2xl font-bold mt-10 mb-4">How We Use Your Information</h2>
                <p>We use the collected information for various purposes, including:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Providing and maintaining our services</li>
                  <li>Notifying you about changes to our services</li>
                  <li>Providing customer support</li>
                  <li>Analyzing usage to improve our offerings</li>
                  <li>Preventing fraud and ensuring security</li>
                  <li>Personalizing your experience</li>
                </ul>
                
                <h2 className="text-2xl font-bold mt-10 mb-4">Data Storage and Security</h2>
                <p>
                  We implement appropriate security measures to protect your personal information from unauthorized access, alteration, disclosure, 
                  or destruction. All data is stored in secure servers located in Canada, and we follow industry best practices for data protection.
                </p>
                
                <h2 className="text-2xl font-bold mt-10 mb-4">Third-Party Services</h2>
                <p>
                  We may employ third-party companies to facilitate our services, perform service-related tasks, or assist us in analyzing how our 
                  services are used. These third parties have access to your information only to perform specific tasks and are obligated to 
                  maintain confidentiality.
                </p>
                
                <h2 className="text-2xl font-bold mt-10 mb-4">Your Data Rights</h2>
                <p>You have the right to:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Access the personal information we have about you</li>
                  <li>Correct inaccurate or incomplete information</li>
                  <li>Request deletion of your data (with certain limitations)</li>
                  <li>Object to our processing of your information</li>
                  <li>Request data portability</li>
                </ul>
                
                <h2 className="text-2xl font-bold mt-10 mb-4">Cookies Policy</h2>
                <p>
                  We use cookies and similar tracking technologies to enhance your browsing experience, analyze site traffic, and personalize content.
                  You can control cookies through your browser settings.
                </p>
                
                <h2 className="text-2xl font-bold mt-10 mb-4">Changes to Privacy Policy</h2>
                <p>
                  We may update our Privacy Policy periodically. We will notify you of any changes by posting the new Privacy Policy on this page 
                  and updating the "Last updated" date.
                </p>
                
                <h2 className="text-2xl font-bold mt-10 mb-4">Contact Us</h2>
                <p>
                  If you have questions or concerns about our Privacy Policy or data practices, please contact us at:
                  <br />
                  <a href="mailto:privacy@AttaboyIntelligence.com" className="text-attaboy hover:underline">
                    privacy@AttaboyIntelligence.com
                  </a>
                </p>
              </div>
            ) : (
              <div className="prose prose-lg max-w-none">
                <p className="text-lg mb-6">Dernière mise à jour: 19 mai 2025</p>
                
                <h2 className="text-2xl font-bold mt-10 mb-4">Introduction</h2>
                <p>
                  Chez Attaboy Intelligence Artificielle (« nous », « notre » ou « nos »), nous respectons votre vie privée et nous nous engageons à protéger vos données personnelles.
                  Cette politique de confidentialité explique comment nous collectons, utilisons et protégeons vos informations lorsque vous visitez notre site Web ou utilisez nos services d'IA.
                </p>
                
                <h2 className="text-2xl font-bold mt-10 mb-4">Informations que nous collectons</h2>
                <p>Nous pouvons collecter plusieurs types d'informations, y compris, mais sans s'y limiter:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Informations personnelles</strong>: Nom, adresse e-mail, numéro de téléphone et autres coordonnées que vous fournissez.</li>
                  <li><strong>Informations d'utilisation</strong>: Comment vous interagissez avec notre site Web et nos services, y compris les fonctionnalités utilisées et le temps passé.</li>
                  <li><strong>Informations sur l'appareil</strong>: Adresse IP, type de navigateur, type d'appareil et système d'exploitation.</li>
                  <li><strong>Données de contenu</strong>: Informations que vous saisissez dans nos outils ou services d'IA.</li>
                </ul>
                
                <h2 className="text-2xl font-bold mt-10 mb-4">Comment nous utilisons vos informations</h2>
                <p>Nous utilisons les informations collectées à diverses fins, notamment:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Fournir et maintenir nos services</li>
                  <li>Vous informer des modifications apportées à nos services</li>
                  <li>Fournir un support client</li>
                  <li>Analyser l'utilisation pour améliorer nos offres</li>
                  <li>Prévenir la fraude et assurer la sécurité</li>
                  <li>Personnaliser votre expérience</li>
                </ul>
                
                <h2 className="text-2xl font-bold mt-10 mb-4">Stockage et sécurité des données</h2>
                <p>
                  Nous mettons en œuvre des mesures de sécurité appropriées pour protéger vos informations personnelles contre tout accès, 
                  modification, divulgation ou destruction non autorisés. Toutes les données sont stockées sur des serveurs sécurisés situés au Canada, 
                  et nous suivons les meilleures pratiques de l'industrie en matière de protection des données.
                </p>
                
                <h2 className="text-2xl font-bold mt-10 mb-4">Services tiers</h2>
                <p>
                  Nous pouvons faire appel à des entreprises tierces pour faciliter nos services, effectuer des tâches liées aux services ou nous aider à analyser comment 
                  nos services sont utilisés. Ces tiers ont accès à vos informations uniquement pour effectuer des tâches spécifiques et sont obligés de 
                  maintenir la confidentialité.
                </p>
                
                <h2 className="text-2xl font-bold mt-10 mb-4">Vos droits en matière de données</h2>
                <p>Vous avez le droit de:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Accéder aux informations personnelles que nous détenons sur vous</li>
                  <li>Corriger des informations inexactes ou incomplètes</li>
                  <li>Demander la suppression de vos données (avec certaines limitations)</li>
                  <li>Vous opposer à notre traitement de vos informations</li>
                  <li>Demander la portabilité des données</li>
                </ul>
                
                <h2 className="text-2xl font-bold mt-10 mb-4">Politique en matière de cookies</h2>
                <p>
                  Nous utilisons des cookies et des technologies de suivi similaires pour améliorer votre expérience de navigation, analyser le trafic du site et personnaliser le contenu.
                  Vous pouvez contrôler les cookies via les paramètres de votre navigateur.
                </p>
                
                <h2 className="text-2xl font-bold mt-10 mb-4">Modifications de la politique de confidentialité</h2>
                <p>
                  Nous pouvons mettre à jour notre politique de confidentialité périodiquement. Nous vous informerons de tout changement en publiant la nouvelle politique de confidentialité sur cette page 
                  et en mettant à jour la date de "Dernière mise à jour".
                </p>
                
                <h2 className="text-2xl font-bold mt-10 mb-4">Nous contacter</h2>
                <p>
                  Si vous avez des questions ou des préoccupations concernant notre politique de confidentialité ou nos pratiques en matière de données, veuillez nous contacter à:
                  <br />
                  <a href="mailto:privacy@AttaboyIntelligenceArtificielle.com" className="text-attaboy hover:underline">
                    privacy@AttaboyIntelligenceArtificielle.com
                  </a>
                </p>
              </div>
            )}
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
};

export default Privacy;
